import React from 'react';
import { Box, Card, Typography, Stack, Skeleton } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis } from 'recharts';
import PropTypes from 'prop-types';
import { tokens } from '../../theme';

export function StatCard({
  label,
  value,
  sublabel,
  sublabelTone = 'neutral',
  sparkline,
  loading = false,
}) {
  const toneStyles = {
    neutral: { color: tokens.color.ink3, icon: null },
    success: { color: tokens.color.success, icon: <TrendingUpIcon sx={{ fontSize: 14 }} /> },
    warning: { color: tokens.color.warning, icon: <WarningAmberIcon sx={{ fontSize: 14 }} /> },
    danger: { color: tokens.color.danger, icon: <TrendingDownIcon sx={{ fontSize: 14 }} /> },
  };
  const tone = toneStyles[sublabelTone] || toneStyles.neutral;

  if (loading) {
    return (
      <Card variant="outlined" sx={{ p: 2.5, height: 140, borderColor: 'divider' }}>
        <Skeleton width="40%" height={18} />
        <Skeleton width="60%" height={36} sx={{ mt: 1.5 }} />
        <Skeleton width="50%" height={18} sx={{ mt: 1 }} />
      </Card>
    );
  }

  return (
    <Card
      variant="outlined"
      sx={{
        p: 2.5,
        height: 140,
        borderColor: 'divider',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: tokens.color.borderHv,
          boxShadow: tokens.shadow.md,
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
        {label}
      </Typography>

      <Typography
        variant="h1"
        sx={{ color: 'text.primary', fontWeight: 600, fontSize: 28, lineHeight: 1.2 }}
      >
        {value}
      </Typography>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
        {sublabel && (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
            {tone.icon && <Box sx={{ color: tone.color, display: 'flex', flexShrink: 0 }}>{tone.icon}</Box>}
            <Typography variant="body2" sx={{ color: tone.color, fontWeight: 500 }} noWrap>
              {sublabel}
            </Typography>
          </Stack>
        )}

        {sparkline && sparkline.length > 0 && (
          <Box sx={{ width: 80, height: 32, flexShrink: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={sparkline.map((v, i) => ({ i, v }))} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
                <XAxis dataKey="i" hide />
                <YAxis hide width={0} />
                <Line
                  type="monotone"
                  dataKey="v"
                  stroke={tokens.color.brand}
                  strokeWidth={1.5}
                  dot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Stack>
    </Card>
  );
}

StatCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  sublabel: PropTypes.string,
  sublabelTone: PropTypes.oneOf(['neutral', 'success', 'warning', 'danger']),
  sparkline: PropTypes.arrayOf(PropTypes.number),
  loading: PropTypes.bool,
};

export default StatCard;
